import { Component, OnInit } from '@angular/core';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { LeaveClaimSummaryInfo } from '../../../../models/api/view/claim/summary/leaveclaimsummaryinfo';
import { BaseComponent } from '../../../base/base.component';
import { MoreButtonComponent } from '../../../core/buttons/more-button.component';
import { ClaimLabelComponent } from '../../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { LeaveScheduleComponent } from '../../leave-schedule/leave-schedule.component';
import { getFieldVisibility } from '../../../../functions/field-security';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-la-claim-card',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ActionRowComponent,
    MoreButtonComponent,
    LeaveScheduleComponent,
    ClaimLabelComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loading ? 'Loading' : 'Loaded'"
      loadingStyle="Box"
      [loadingHeight]="742"
      [error]="error"
    >
      <ng-template #loaded>
        <div class="tw-w-full">
          <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
          >
            <app-detail-panel
              [dataSource]="leaveClaimSummaryInfo"
              class="tw-w-full "
            >
              <app-detail-item label="Claim Number" name="fileNumber">
                <ng-template let-data #value>
                  <app-claim-label [claim]="claimRow" [readOnly]="true" />
                </ng-template>
              </app-detail-item>
              <app-detail-item label="Examiner" name="examinerName" />
              <app-detail-item label="Status" name="absenceStatus">
                <ng-template let-data #value>
                  <app-tag [type]="statusType">{{
                    data.activeStatus + ' - ' + data.absenceStatus
                  }}</app-tag>
                </ng-template>
              </app-detail-item>
              <app-detail-item label="Type" name="absenceType" />
              @if (showSubType) {
                <app-detail-item label="Subtype" name="absenceSubtype" />
              }

              <app-detail-item label="Substatus" name="absenceSubstatus" />
            </app-detail-panel>

            <app-detail-panel
              [dataSource]="leaveClaimSummaryInfo"
              class="tw-w-full "
            >
              <app-detail-item label="Caused By" name="causedBy" />
              <app-detail-item label="Case Type" name="caseType" />
              @if (showConditionType) {
                <app-detail-item label="Condition Type" name="conditionType" />
              }
              <app-detail-item
                label="FMLA calculation method"
                name="fmlaCalculation"
                labelCase="none"
              />
              <app-detail-item
                label="Spouse at same client"
                name="spouseAtSameClient"
              />
            </app-detail-panel>
          </div>

          <div
            class="tw-font-bold tw-text-xl tw-pt-2 tw-text-[var(--sed-black-80-color)] tw-pb-1 tw-border-b"
          >
            Dates
          </div>
          <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
          >
            <app-detail-panel
              [dataSource]="leaveClaimSummaryInfo"
              class="tw-w-full "
            >
              <app-detail-item
                label="Absence From"
                name="absenceFrom"
                format="d"
              />
              <app-detail-item label="Absence To" name="absenceTo" format="d" />
              <app-detail-item
                label="Estimated RTW"
                name="estimatedRTW"
                format="d"
                labelCase="none"
                [showEmpty]="showEmptyRtw"
              />
              <app-detail-item label="Hours Missed" name="hoursMissed" />

              <app-detail-item
                label="Exhaustion Date"
                name="exhaustionDate"
                format="d"
              />
            </app-detail-panel>

            <app-detail-panel
              [dataSource]="leaveClaimSummaryInfo"
              class="tw-w-full "
              [showEmpty]="false"
            >
              <app-detail-item
                label="Approved From"
                name="approvedFrom"
                format="d"
              />
              <app-detail-item
                label="Approved To"
                name="approvedTo"
                format="d"
              />

              <app-detail-item
                label="Actual RTW"
                name="actualRTW"
                format="d"
                labelCase="none"
                [showEmpty]="showEmptyRtw"
              />
              <app-detail-item label="Hours Approved" name="hoursApproved" />

              <app-detail-item
                label="Hours Worked in Last 12 Months"
                name="hoursWorked"
              />
            </app-detail-panel>
          </div>

          <div class="tw-py-8">
            <app-leave-schedule />
          </div>

          <app-action-row>
            <app-more-button
              right1
              (click)="this.view.navigateCurrentClaim(['absence'])"
              [ariaLabel]="
                'Navigate to more actions for claim ' + claimRow?.claimNumber
              "
            />
          </app-action-row>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class LaClaimCardComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  leaveClaimSummaryInfo: LeaveClaimSummaryInfo;
  showConditionType = true;
  showSubType = true;
  showEmptyRtw = false;
  statusType: TagType;

  loading = false;

  error: any = null;

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    const visibility = getFieldVisibility(
      this.session.user?.fieldSecurity?.restrictedFields,
    );
    this.showConditionType = visibility.showConditionType;
    this.showSubType = visibility.showSubType;

    try {
      this.claimRow = this.view.currentClaim();

      if (this.claimRow.lineOfBusiness === 'LA') {
        this.leaveClaimSummaryInfo =
          await this.api.claimSummary.getLeaveClaimSummaryInfo(
            this.claimRow.recordSource,
            this.claimRow.recordId,
          );
        // Trim spaces from examiner name
        if (
          this.leaveClaimSummaryInfo.firstExaminerName ||
          this.leaveClaimSummaryInfo.lastExaminerName
        ) {
          const first =
            this.leaveClaimSummaryInfo.firstExaminerName?.trim() ?? '';
          const last =
            this.leaveClaimSummaryInfo.lastExaminerName?.trim() ?? '';
          this.leaveClaimSummaryInfo.examinerName = `${first} ${last}`.trim();
        }
        this.statusType =
          this.leaveClaimSummaryInfo.activeStatus === 'Open'
            ? ('Success' as TagType)
            : ('Error' as TagType);
        if (this.leaveClaimSummaryInfo.hoursWorked)
          this.leaveClaimSummaryInfo.hoursWorked = this.formatHoursWorked(
            this.leaveClaimSummaryInfo.hoursWorked,
          );
        this.showEmptyRtw = this.isContinuousCaseType(
          this.leaveClaimSummaryInfo.caseType,
        );
      }
    } catch (error) {
      console.error(error);
      this.error = 'Leave data currently unavailable';
    }
  }

  private isContinuousCaseType(caseType: string | null | undefined): boolean {
    const normalized = (caseType ?? '').trim().toLowerCase();
    return normalized === 'continuous' || normalized === 'continous';
  }

  private formatHoursWorked(value: string | number): string {
    const num = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(num)) {
      return String(value);
    }
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

}
